// src/utils/gamification.js
// Utility module for handling Ebenezer Books gamification:
// history tracking, daily goals, streaks, last-30-days activity, and badges.

// Streak unlock milestones for premium books
export const PREMIUM_LOCKS = {};

/**
 * Standard Manager for all Gamification and Reading progress logic.
 */
export class GamificationManager {
  /**
   * Helper to get local YYYY-MM-DD date string
   * @param {Date} date
   * @returns {string} YYYY-MM-DD
   */
  static getLocalDateString(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /**
   * Retrieve the reading history object from localStorage
   * @returns {Object}
   */
  static getReadingHistory() {
    try {
      const raw = localStorage.getItem("ebenezer_reading_history");
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      console.error("Error reading ebenezer_reading_history:", e);
      return {};
    }
  }

  /**
   * Log a page view activity
   * @param {Object} book
   * @param {number|string} pageNum
   * @param {boolean} isSpread
   * @param {number} totalPages
   * @returns {Array} List of newly unlocked badges
   */
  static recordPageView(book, pageNum, isSpread, totalPages) {
    if (!book || !book.id) return [];

    const history = this.getReadingHistory();
    const todayStr = this.getLocalDateString();
    const now = new Date();
    const hour = now.getHours();

    if (!history[todayStr]) {
      history[todayStr] = {
        pages: {},
        timestamps: []
      };
    }

    if (!history[todayStr].pages[book.id]) {
      history[todayStr].pages[book.id] = [];
    }

    // Determine which page numbers to record
    const pagesToRecord = [Number(pageNum)];
    if (isSpread && (Number(pageNum) + 1) <= Number(totalPages || 9999)) {
      pagesToRecord.push(Number(pageNum) + 1);
    }

    let updated = false;
    pagesToRecord.forEach((p) => {
      if (!history[todayStr].pages[book.id].includes(p)) {
        history[todayStr].pages[book.id].push(p);
        updated = true;
      }
    });

    if (updated) {
      // Record timestamp detail
      history[todayStr].timestamps.push({
        bookId: book.id,
        page: pageNum,
        hour: hour,
        time: now.getTime()
      });

      localStorage.setItem("ebenezer_reading_history", JSON.stringify(history));
      localStorage.setItem("ebenezer_progress_last_updated", String(Date.now()));

      // After updating, check if any new badges were unlocked
      return this.checkNewUnlocks(history);
    }

    return [];
  }

  /**
   * Calculate current and longest streaks (in consecutive days)
   * @param {Object} history
   * @returns {Object} { currentStreak, longestStreak }
   */
  static calculateStreak(history = this.getReadingHistory()) {
    const activeDates = Object.keys(history).filter((dateStr) => {
      // A day is active if at least one book had pages read
      const day = history[dateStr];
      if (!day || !day.pages) return false;
      return Object.values(day.pages).some((pArray) => pArray && pArray.length > 0);
    });

    if (activeDates.length === 0) {
      return { currentStreak: 0, longestStreak: 0 };
    }

    // Parse strings to timestamp dates at midnight UTC to avoid timezone issues
    const dates = activeDates
      .map((dStr) => {
        const [y, m, d] = dStr.split("-").map(Number);
        return Date.UTC(y, m - 1, d);
      })
      .sort((a, b) => a - b);

    const MS_PER_DAY = 86400000;

    // Compute Longest Streak
    let longestStreak = 1;
    let currentStreakRun = 1;

    for (let i = 1; i < dates.length; i++) {
      const diff = dates[i] - dates[i - 1];
      if (diff === MS_PER_DAY) {
        currentStreakRun++;
      } else if (diff > MS_PER_DAY) {
        if (currentStreakRun > longestStreak) {
          longestStreak = currentStreakRun;
        }
        currentStreakRun = 1;
      }
    }
    if (currentStreakRun > longestStreak) {
      longestStreak = currentStreakRun;
    }

    // Compute Current Streak
    const today = new Date();
    const todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayUTC = todayUTC - MS_PER_DAY;

    const datesSet = new Set(dates);
    let currentStreak = 0;

    if (datesSet.has(todayUTC)) {
      currentStreak = 1;
      let checkDate = todayUTC - MS_PER_DAY;
      while (datesSet.has(checkDate)) {
        currentStreak++;
        checkDate -= MS_PER_DAY;
      }
    } else if (datesSet.has(yesterdayUTC)) {
      currentStreak = 1;
      let checkDate = yesterdayUTC - MS_PER_DAY;
      while (datesSet.has(checkDate)) {
        currentStreak++;
        checkDate -= MS_PER_DAY;
      }
    }

    return { currentStreak, longestStreak };
  }

  /**
   * Calculate daily progress
   * @param {Object} history
   * @returns {Object} { pagesReadToday, goal, percent }
   */
  static getDailyGoalProgress(history = this.getReadingHistory()) {
    let goal = 10;
    try {
      const savedGoal = localStorage.getItem("ebenezer_daily_goal");
      if (savedGoal) goal = parseInt(savedGoal, 10) || 10;
    } catch (e) {}

    const todayStr = this.getLocalDateString();
    const todayData = history[todayStr];
    let pagesReadToday = 0;

    if (todayData && todayData.pages) {
      Object.values(todayData.pages).forEach((pArray) => {
        pagesReadToday += (pArray || []).length;
      });
    }

    const percent = Math.min(100, Math.round((pagesReadToday / goal) * 100));

    return { pagesReadToday, goal, percent };
  }

  /**
   * Generate last 30 days history for activity calendar
   * @param {Object} history
   * @returns {Array} Array of day activity objects
   */
  static getLast30DaysActivity(history = this.getReadingHistory()) {
    const result = [];
    const today = new Date();

    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dStr = this.getLocalDateString(d);
      
      let count = 0;
      const dayData = history[dStr];
      if (dayData && dayData.pages) {
        Object.values(dayData.pages).forEach((pArray) => {
          count += (pArray || []).length;
        });
      }

      // Determine level: 0 (none), 1 (1-4 pages), 2 (5-9 pages), 3 (10+ pages)
      let level = 0;
      if (count > 0 && count < 5) level = 1;
      else if (count >= 5 && count < 10) level = 2;
      else if (count >= 10) level = 3;

      // Format human-readable date (e.g. May 27)
      const formattedDate = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

      result.push({
        date: dStr,
        formattedDate,
        count,
        level
      });
    }

    return result;
  }

  /**
   * Get list of all achievements with their locked/unlocked state
   * @param {Object} history
   * @param {Array} books
   * @returns {Array}
   */
  static checkAchievements(history = this.getReadingHistory(), books) {
    const uniquePagesSet = new Set();

    // Aggregate stats across history
    Object.keys(history).forEach((dateStr) => {
      const day = history[dateStr];
      if (day && day.pages) {
        Object.entries(day.pages).forEach(([bookId, pArray]) => {
          pArray.forEach((p) => {
            uniquePagesSet.add(`${bookId}_${p}`);
          });
        });
      }
    });

    const totalUniquePages = uniquePagesSet.size;

    // Badges catalog definition
    const BADGES = [
      {
        id: "first-steps",
        title: "First Steps",
        description: "Read your first page in the web reader.",
        icon: "🚶",
        check: (hist, bList) => totalUniquePages > 0
      },
      {
        id: "streak-3",
        title: "Habit Builder",
        description: "Reach a 3-day reading streak.",
        icon: "🔥",
        check: (hist, bList) => {
          const streaks = this.calculateStreak(hist);
          return streaks.longestStreak >= 3 || streaks.currentStreak >= 3;
        }
      },
      {
        id: "streak-7",
        title: "Weekly Warrior",
        description: "Reach a 7-day reading streak to claim a free physical book reward.",
        icon: "⚡",
        check: (hist, bList) => {
          const streaks = this.calculateStreak(hist);
          return streaks.longestStreak >= 7 || streaks.currentStreak >= 7;
        }
      },
      {
        id: "streak-14",
        title: "Devoted Reader",
        description: "Reach a 14-day reading streak to claim a free physical premium book reward.",
        icon: "👑",
        check: (hist, bList) => {
          const streaks = this.calculateStreak(hist);
          return streaks.longestStreak >= 14 || streaks.currentStreak >= 14;
        }
      },
      {
        id: "streak-30",
        title: "Streak Legend",
        description: "Reach a 30-day reading streak to claim a free physical deluxe book reward.",
        icon: "👑",
        check: (hist, bList) => {
          const streaks = this.calculateStreak(hist);
          return streaks.longestStreak >= 30 || streaks.currentStreak >= 30;
        }
      },
      {
        id: "theologian",
        title: "Theology Scholar",
        description: "Read at least 5 pages of any Theology book.",
        icon: "🕊️",
        check: (hist, bList) => {
          if (!bList) return false;
          const theologyBookIds = bList
            .filter((b) => b.genre && b.genre.toLowerCase().includes("theology"))
            .map((b) => b.id);
          
          let theologyPagesRead = 0;
          Object.keys(hist).forEach((dStr) => {
            const day = hist[dStr];
            if (day && day.pages) {
              theologyBookIds.forEach((bId) => {
                if (day.pages[bId]) {
                  theologyPagesRead += day.pages[bId].length;
                }
              });
            }
          });
          return theologyPagesRead >= 5;
        }
      },
      {
        id: "early-bird",
        title: "Early Bird",
        description: "Read a book page early in the morning (5:00 AM - 7:59 AM).",
        icon: "🌅",
        check: (hist, bList) => {
          return Object.values(hist).some((day) => {
            return day.timestamps?.some((t) => t.hour >= 5 && t.hour < 8);
          });
        }
      },
      {
        id: "night-owl",
        title: "Night Owl",
        description: "Read a book page late at night (10:00 PM - 3:59 AM).",
        icon: "🦉",
        check: (hist, bList) => {
          return Object.values(hist).some((day) => {
            return day.timestamps?.some((t) => t.hour >= 22 || t.hour < 4);
          });
        }
      },
      {
        id: "finisher",
        title: "Finisher",
        description: "Read at least 85% of pages of any book.",
        icon: "🎓",
        check: (hist, bList) => {
          if (!bList) return false;
          return bList.some((book) => {
            const totalPages = book.pages || 100;
            const uniquePagesForBook = new Set();
            Object.values(hist).forEach((day) => {
              if (day?.pages?.[book.id]) {
                day.pages[book.id].forEach(p => uniquePagesForBook.add(p));
              }
            });
            const requiredPages = Math.max(1, Math.ceil(totalPages * 0.85));
            return uniquePagesForBook.size >= requiredPages;
          });
        }
      },
      {
        id: "polymath",
        title: "Polymath",
        description: "Read books from 3 different genres.",
        icon: "🧭",
        check: (hist, bList) => {
          if (!bList) return false;
          const readGenres = new Set();
          Object.values(hist).forEach((day) => {
            if (day && day.pages) {
              Object.keys(day.pages).forEach((bookId) => {
                const book = bList.find((b) => b.id === bookId);
                if (book && book.genre && day.pages[bookId].length > 0) {
                  readGenres.add(book.genre.toLowerCase());
                }
              });
            }
          });
          return readGenres.size >= 3;
        }
      }
    ];

    // Resolve book list if we have it, otherwise fallback to parameter
    let resolvedBooks = books || [];
    try {
      if (!resolvedBooks.length && window.__EBENEZER_BOOKS_LIST__) {
        resolvedBooks = window.__EBENEZER_BOOKS_LIST__;
      }
    } catch (e) {}

    return BADGES.map((b) => {
      let unlocked = false;
      try {
        unlocked = b.check(history, resolvedBooks.length ? resolvedBooks : null);
      } catch (e) {
        console.error(`Error checking badge ${b.id}:`, e);
      }
      return {
        id: b.id,
        title: b.title,
        description: b.description,
        icon: b.icon,
        unlocked
      };
    });
  }

  /**
   * Core achievement check returning only newly unlocked badges
   * @param {Object} history
   * @returns {Array} List of newly unlocked badge objects
   */
  static checkNewUnlocks(history) {
    const badges = this.checkAchievements(history);
    const newlyUnlocked = [];

    let unlockedIds = [];
    try {
      const raw = localStorage.getItem("ebenezer_unlocked_badges");
      unlockedIds = raw ? JSON.parse(raw) : [];
    } catch (e) {
      unlockedIds = [];
    }

    const updatedIds = [...unlockedIds];

    badges.forEach((badge) => {
      if (badge.unlocked && !unlockedIds.includes(badge.id)) {
        newlyUnlocked.push(badge);
        updatedIds.push(badge.id);
      }
    });

    if (newlyUnlocked.length > 0) {
      localStorage.setItem("ebenezer_unlocked_badges", JSON.stringify(updatedIds));
      localStorage.setItem("ebenezer_progress_last_updated", String(Date.now()));
    }

    return newlyUnlocked;
  }

  /**
   * Helper to verify if a book is unlocked (online titles are always free & unlocked)
   * @param {Object} book
   * @param {Object} history
   * @returns {boolean}
   */
  static isBookUnlocked(book, history = this.getReadingHistory()) {
    return true;
  }
}

// ── Backward compatibility wrappers ──────────────────────────────────────────
export const getLocalDateString = (date) => GamificationManager.getLocalDateString(date);
export const getReadingHistory = () => GamificationManager.getReadingHistory();
export const recordPageView = (book, pageNum, isSpread, totalPages) =>
  GamificationManager.recordPageView(book, pageNum, isSpread, totalPages);
export const calculateStreak = (history) => GamificationManager.calculateStreak(history);
export const getDailyGoalProgress = (history) => GamificationManager.getDailyGoalProgress(history);
export const getLast30DaysActivity = (history) => GamificationManager.getLast30DaysActivity(history);
export const checkAchievements = (history, books) => GamificationManager.checkAchievements(history, books);
export const checkNewUnlocks = (history) => GamificationManager.checkNewUnlocks(history);
export const isBookUnlocked = (book, history) => GamificationManager.isBookUnlocked(book, history);
